import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE_ORIGIN, WEBSITE_ID, resolveMeta } from "@/lib/seo";

// Injects a per-page WebPage + BreadcrumbList JSON-LD graph that references the
// site-wide entities (Person, WebSite, Albums, Podcasts) declared in index.html.
// Also keeps <title> and <link rel="canonical"> in sync on route changes.
export default function SeoSchema() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = resolveMeta(pathname);
    const canonical = `${SITE_ORIGIN}${pathname === "/" ? "/" : pathname}`;

    const webpage = {
      "@type": "WebPage",
      "@id": `${canonical}#webpage`,
      url: canonical,
      name: meta.name,
      isPartOf: { "@id": WEBSITE_ID },
      inLanguage: "en-US",
      breadcrumb: { "@id": `${canonical}#breadcrumb` },
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", "h2"],
      },
    };
    if (meta.about) webpage.about = meta.about;
    if (meta.mainEntity) webpage.mainEntity = meta.mainEntity;

    const breadcrumb = {
      "@type": "BreadcrumbList",
      "@id": `${canonical}#breadcrumb`,
      itemListElement: meta.crumbs.map(([label, path], i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: label,
        item: `${SITE_ORIGIN}${path === "/" ? "/" : path}`,
      })),
    };

    const data = {
      "@context": "https://schema.org",
      "@graph": [webpage, breadcrumb],
    };

    let el = document.getElementById("page-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "page-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);

    document.title = meta.name;
    const link = document.querySelector('link[rel="canonical"]');
    if (link) link.setAttribute("href", canonical);
  }, [pathname]);

  return null;
}
